import React, { useEffect, useRef, useState } from "react";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HandshakeIcon from "@mui/icons-material/Handshake";
import GroupsIcon from "@mui/icons-material/Groups";
import { useLocalization } from "@fluent/react";
import { useAppSelector } from "../../../../app/hooks";
import { selectLoggedUser } from "../../../user/userSlice";
import { ValidationProcessStepHeader } from "../../validationProcess/components/ValidationProcessStepHeader";
import { ValidationTeamEditor } from "../../validationProcess/components/ValidationTeamEditor";
import { ProjectExecutionType, ProjectExecutionTypeEnum, ProjectTypeEnum } from "../../projectSlice";
import { CreateValidationProjectWizardPayload, ValidationProjectTeamState } from "../validationProjectSlice";

const tabs = [
    "validation-project-wizard-tab-details",
    "validation-project-wizard-tab-mode",
    "validation-project-wizard-tab-team",
] as const;

const buildSuggestedProjectNumber = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `PROJ-VAL-${year}-${month}-${day}`;
};

type TeamState = {
    manager: ValidationProjectTeamState["manager"];
    members: ValidationProjectTeamState["members"];
};

export interface ValidationProjectWizardProps {
    open: boolean;
    onClose: () => void;
    onSave: (payload: CreateValidationProjectWizardPayload) => void | Promise<void>;
    initialValue?: Partial<CreateValidationProjectWizardPayload>;
}

const choiceCard = (selected: boolean) => ({
    position: "relative",
    p: 1.5,
    border: 1.5,
    borderColor: selected ? "success.main" : "divider",
    borderRadius: 2,
    cursor: "pointer",
    bgcolor: "background.paper",
    transition: "border-color .15s ease, transform .05s ease, background-color .15s ease",
    "&:hover": {
        borderColor: selected ? "success.main" : "text.disabled",
        bgcolor: "background.paper",
    },
    "&:active": {
        transform: "scale(0.997)",
    },
});

const executionTypeLabel = (type: ProjectExecutionType, l10n: ReturnType<typeof useLocalization>["l10n"]) => {
    switch (type) {
        case ProjectExecutionTypeEnum.INTERNAL:
            return l10n.getString("validation-project-wizard-mode-internal", null, "Internal");
        case ProjectExecutionTypeEnum.FOR_EXTERNAL_CUSTOMER:
            return l10n.getString("validation-project-wizard-mode-external", null, "For external customer");
        case ProjectExecutionTypeEnum.JOINT:
            return l10n.getString("validation-project-wizard-mode-joint", null, "Joint");
        default:
            return "-";
    }
};

const ValidationProjectWizard: React.FC<ValidationProjectWizardProps> = ({ open, onClose, onSave, initialValue }) => {
    const theme = useTheme();
    const { l10n } = useLocalization();
    const loggedUser = useAppSelector(selectLoggedUser);

    const [activeTab, setActiveTab] = useState(0);
    const [projectNo, setProjectNo] = useState(initialValue?.no ?? "");
    const [projectName, setProjectName] = useState(initialValue?.name ?? "");
    const [plannedStart, setPlannedStart] = useState(initialValue?.plannedStart ?? "");
    const [plannedEnd, setPlannedEnd] = useState(initialValue?.plannedEnd ?? "");
    const [executionType, setExecutionType] = useState<ProjectExecutionType | null>(initialValue?.executionType ?? null);
    const [team, setTeam] = useState<TeamState>(initialValue?.team ?? { manager: null, members: [] });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const appliedDefaultManager = useRef(false);

    useEffect(() => {
        if (!open) return;

        setActiveTab(0);
        setProjectNo(initialValue?.no ?? buildSuggestedProjectNumber());
        setProjectName(initialValue?.name ?? "");
        setPlannedStart(initialValue?.plannedStart ?? "");
        setPlannedEnd(initialValue?.plannedEnd ?? "");
        setExecutionType(initialValue?.executionType ?? null);
        setTeam(initialValue?.team ?? { manager: null, members: [] });
        setSaving(false);
        setError(null);
        appliedDefaultManager.current = false;
    }, [open, initialValue]);

    useEffect(() => {
        if (!open) return;
        if (!appliedDefaultManager.current && loggedUser && !team.manager) {
            setTeam((state) => ({ ...state, manager: String(loggedUser) }));
            appliedDefaultManager.current = true;
        }
    }, [loggedUser, open, team.manager]);

    const dateRangeError = plannedStart && plannedEnd && plannedEnd < plannedStart
        ? l10n.getString("validation-project-wizard-date-range-error", null, "Planned end date cannot be earlier than planned start date.")
        : null;

    const validateTab = (tabIndex: number): string | null => {
        if (tabIndex === 0) {
            if (!projectNo.trim()) {
                return l10n.getString("field-is-required", null, "Field is required");
            }
            if (!projectName.trim()) {
                return l10n.getString("field-is-required", null, "Field is required");
            }
            if (dateRangeError) {
                return dateRangeError;
            }
        }

        if (tabIndex === 1 && executionType === null) {
            return l10n.getString("validation-project-wizard-mode-required", null, "Select project mode.");
        }

        return null;
    };

    const handleTabChange = (_: React.SyntheticEvent, nextTab: number) => {
        if (nextTab <= activeTab) {
            setActiveTab(nextTab);
            return;
        }

        const validationError = validateTab(activeTab);
        if (validationError) {
            setError(validationError);
            return;
        }

        setActiveTab(nextTab);
    };

    const handleNext = () => {
        const validationError = validateTab(activeTab);
        if (validationError) {
            setError(validationError);
            return;
        }

        setActiveTab((tab) => Math.min(tab + 1, tabs.length - 1));
    };

    const handleBack = () => setActiveTab((tab) => Math.max(tab - 1, 0));

    const handleSave = async () => {
        const validationError = validateTab(0) ?? validateTab(1) ?? validateTab(2);
        if (validationError || executionType === null) {
            setError(validationError ?? l10n.getString("validation-project-wizard-mode-required", null, "Select project mode."));
            return;
        }

        setSaving(true);
        try {
            await onSave({
                type: ProjectTypeEnum.VALIDATION,
                no: projectNo.trim(),
                name: projectName.trim(),
                plannedStart: plannedStart || null,
                plannedEnd: plannedEnd || null,
                executionType,
                team,
            });
        } catch (saveError: any) {
            setError(saveError?.message ?? l10n.getString("error", null, "Error"));
        } finally {
            setSaving(false);
        }
    };

    const handleDialogClose = (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return;
        onClose();
    };

    const summaryChips = [
        projectNo.trim() ? { label: `${l10n.getString("project-no", null, "Project no")}: ${projectNo.trim()}`, color: "success" as const } : null,
        projectName.trim() ? { label: `${l10n.getString("project-title", null, "Project title")}: ${projectName.trim()}`, color: "success" as const } : null,
        executionType !== null ? { label: executionTypeLabel(executionType, l10n), color: "info" as const } : null,
        team.members.length ? { label: `${l10n.getString("project-team", null, "Team")}: ${team.members.length}`, color: "default" as const } : null,
    ].filter(Boolean) as Array<{ label: string; color: "default" | "success" | "info" }>;

    const renderExecutionTypeCard = (
        value: ProjectExecutionTypeEnum,
        icon: React.ReactNode,
        title: string,
        description: string,
    ) => {
        const selected = executionType === value;

        return (
            <Box onClick={() => setExecutionType(value)} sx={{ ...choiceCard(selected), width: "100%" }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    {selected
                        ? <CheckCircleIcon sx={{ color: "success.main" }} />
                        : <RadioButtonUncheckedIcon sx={{ color: "text.disabled" }} />}
                    <Box sx={{ color: selected ? "success.main" : "text.secondary" }}>{icon}</Box>
                    <Box>
                        <Typography fontWeight={700} variant="body2">{title}</Typography>
                        <Typography variant="caption" color="text.secondary">{description}</Typography>
                    </Box>
                </Stack>
            </Box>
        );
    };

    const renderTabContent = () => {
        if (activeTab === 0) {
            return (
                <Stack spacing={2.5}>
                    <ValidationProcessStepHeader
                        title={l10n.getString("validation-project-wizard-tab-details", null, "Project details")}
                    />
                    <TextField
                        value={projectNo}
                        onChange={(event) => setProjectNo(event.target.value)}
                        label={l10n.getString("project-no", null, "Project no")}
                        required
                        fullWidth
                        error={!projectNo.trim()}
                        helperText={!projectNo.trim() ? l10n.getString("field-is-required", null, "Field is required") : " "}
                    />
                    <TextField
                        value={projectName}
                        onChange={(event) => setProjectName(event.target.value)}
                        label={l10n.getString("project-title", null, "Project title")}
                        required
                        fullWidth
                        error={!projectName.trim()}
                        helperText={!projectName.trim() ? l10n.getString("field-is-required", null, "Field is required") : " "}
                    />
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <TextField
                            value={plannedStart}
                            onChange={(event) => setPlannedStart(event.target.value)}
                            label={l10n.getString("validation-project-wizard-planned-start", null, "Planned start")}
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(dateRangeError)}
                            helperText={dateRangeError ? dateRangeError : " "}
                        />
                        <TextField
                            value={plannedEnd}
                            onChange={(event) => setPlannedEnd(event.target.value)}
                            label={l10n.getString("validation-project-wizard-planned-end", null, "Planned end")}
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(dateRangeError)}
                            helperText={dateRangeError ? dateRangeError : " "}
                        />
                    </Stack>
                </Stack>
            );
        }

        if (activeTab === 1) {
            return (
                <Stack spacing={2.5}>
                    <ValidationProcessStepHeader
                        title={l10n.getString("validation-project-wizard-tab-mode", null, "Project mode")}
                    />
                    <Stack spacing={1.5}>
                        {renderExecutionTypeCard(
                            ProjectExecutionTypeEnum.INTERNAL,
                            <ApartmentIcon />,
                            l10n.getString("validation-project-wizard-mode-internal", null, "Internal"),
                            l10n.getString("validation-project-wizard-mode-internal-description", null, "The project is carried out internally within your organization."),
                        )}
                        {renderExecutionTypeCard(
                            ProjectExecutionTypeEnum.FOR_EXTERNAL_CUSTOMER,
                            <HandshakeIcon />,
                            l10n.getString("validation-project-wizard-mode-external", null, "For external customer"),
                            l10n.getString("validation-project-wizard-mode-external-description", null, "The project is delivered for an external customer."),
                        )}

                    </Stack>
                </Stack>
            );
        }

        return (
            <Stack spacing={2.5}>
                <ValidationProcessStepHeader
                    title={l10n.getString("validation-project-wizard-tab-team", null, "Team")}
                />
                <ValidationTeamEditor
                    manager={team.manager}
                    members={team.members}
                    onChange={setTeam}
                />
            </Stack>
        );
    };

    return (
        <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {l10n.getString("validation-project-wizard-title", null, "New validation project")}
                <IconButton onClick={onClose}><Close /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 1.5, mt: 1 }}>
                <Box
                    sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 2,
                        overflow: "hidden",
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            bgcolor: alpha(theme.palette.grey[500], 0.08),
                            borderBottom: 1,
                            borderColor: "divider",
                            minHeight: 42,
                            "& .MuiTab-root": {
                                minHeight: 42,
                                py: 0.5,
                                fontSize: theme.typography.pxToRem(13),
                            },
                        }}
                    >
                        {tabs.map((tabId) => (
                            <Tab key={tabId} label={l10n.getString(tabId, null, tabId)} />
                        ))}
                    </Tabs>
                    <Box sx={{ p: 2.5 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                            {summaryChips.map((chip) => (
                                <Chip key={chip.label} label={chip.label} size="small" color={chip.color} variant={chip.color === "default" ? "outlined" : "filled"} />
                            ))}
                        </Stack>
                        {renderTabContent()}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: alpha(theme.palette.grey[500], 0.08), justifyContent: "space-between", px: 3, py: 2 }}>
                <Button onClick={onClose}>{l10n.getString("cancel", null, "Cancel")}</Button>
                <Stack direction="row" spacing={1}>
                    <Button onClick={handleBack} disabled={activeTab === 0}>
                        {l10n.getString("back", null, "Back")}
                    </Button>
                    {activeTab < tabs.length - 1 ? (
                        <Button variant="contained" onClick={handleNext}>
                            {l10n.getString("next", null, "Next")}
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={18} color="inherit" />
                                    <span>{l10n.getString("saving", null, "Saving...")}</span>
                                </Stack>
                            ) : l10n.getString("validation-project-wizard-create", null, "Create project")}
                        </Button>
                    )}
                </Stack>
            </DialogActions>
            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="error" variant="filled" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default ValidationProjectWizard;